/// <reference types="google.maps" />

import { FilterPropertyDto, GeoCoordinateDto, BoundaryDto, MachineLocationSetV2Dto, LocationDataV2Dto } from "../api/exa";

// import { BoundaryDto, FilterPropertyDto, GeoCoordinateDto, LocationDataV2Dto, MachineLocationSetV2Dto } from 'src/app/api/generated';

declare const google: any;

export class HelperFunctions {
  static copy<T>(object?: T): T | undefined {
    if (object) {
      return JSON.parse(JSON.stringify(object)) as T;
    }
    return undefined;
  }

  static copyList<T>(objectList?: T[]): T[] | undefined {
    if (objectList) {
      const copy: T[] = [];
      objectList.forEach((val: T) => {
        const cp = HelperFunctions.copy<T>(val);
        if (cp) {
          copy.push(cp);
        }
      });
      return copy;
    }
    return undefined;
  }

  static isSameFilter(filterA?: FilterPropertyDto[], filterB?: FilterPropertyDto[]) {
    if (!filterA && !filterB) {
      return true;
    }
    if ((!filterA && filterB) || (filterA && !filterB)) {
      return false;
    }
    if (filterA && filterB) {
      if (filterA.length != filterB.length) {
        return false;
      }
      // sort both by ascending 'names'
      filterA.sort((x, y) => x.name!.localeCompare(y.name!));
      filterB.sort((x, y) => x.name!.localeCompare(y.name!));

      for (let i = 0; i < filterA.length; i++) {
        const filterAElement: FilterPropertyDto = filterA[i];
        const filterBElement: FilterPropertyDto = filterB[i];
        if (filterAElement.name != filterBElement.name || filterAElement.operator != filterBElement.operator) {
          return false;
        }
        if ((!filterAElement.values && filterBElement.values) || (filterAElement.values && !filterBElement.values)) {
          return false;
        }
        // check values
        if (filterAElement.values && filterBElement.values) {
          if (filterAElement.values.length != filterBElement.values.length) {
            return false;
          }
          for (let j = 0; j < filterAElement.values.length; j++) {
            const filterAElementValue = filterAElement.values[j];
            const filterBElementValue = filterBElement.values[j];
            if (filterAElementValue != filterBElementValue) {
              return false;
            }
          }
        } else {
          return false;
        }
      }
      return true;
    } else {
      return false;
    }
  }

  static getHa(latLngPath: google.maps.LatLng[]): number {
    if (latLngPath && latLngPath.length > 2) {
      // computeArea result is in square meter
      return Math.round(google.maps.geometry.spherical.computeArea(latLngPath) as number) * 0.0001;
    }
    return 0;
  }

  static getHaLatLngLiteral(path: google.maps.LatLngLiteral[]): number {
    const latLngPath: google.maps.LatLng[] = [];
    if (path && path.length > 2) {
      for (const p of path) {
        const position: google.maps.LatLng = new google.maps.LatLng(p);
        latLngPath.push(position);
      }
    }
    return this.getHa(latLngPath);
  }

  static getDistance(a: google.maps.LatLngLiteral, b: google.maps.LatLngLiteral): number {
    const EarthRadiusMeters = 6378137.0; // meters
    const dLat = ((b.lat - a.lat) * Math.PI) / 180;
    const dLon = ((b.lng - a.lng) * Math.PI) / 180;
    const angle =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((a.lat * Math.PI) / 180) * Math.cos((a.lat * Math.PI) / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(angle), Math.sqrt(1 - angle));
    const distance = EarthRadiusMeters * c;
    return distance;
  }

  static computeCentroid(boundary: GeoCoordinateDto[]): GeoCoordinateDto {
    let latitude = 0;
    let longitude = 0;
    const n = boundary.length;
    let signedArea = 0;

    // For all vertices
    for (let i = 0; i < n; i++) {
      const m0 = boundary[i];
      const m1 = boundary[(i + 1) % n];

      const x0 = m0.latitude!;
      const y0 = m0.longitude!;
      const x1 = m1.latitude!;
      const y1 = m1.longitude!;

      // Calculate value of A
      // using shoelace formula
      const A = x0 * y1 - x1 * y0;
      signedArea += A;

      // Calculating coordinates of
      // centroid of polygon
      latitude += (x0 + x1) * A;
      longitude += (y0 + y1) * A;
    }

    signedArea *= 0.5;
    latitude = latitude / (6 * signedArea);
    longitude = longitude / (6 * signedArea);

    return { latitude: latitude, longitude: longitude };
  }

  private static escapeRegExp(regExpStr: string) {
    return regExpStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
  }

  static replaceAll(inputString: string, find: string, replace: string): string {
    return inputString.replace(new RegExp(HelperFunctions.escapeRegExp(find), 'g'), replace);
  }

  static b64toBlob(b64Data: string, contentType = '', sliceSize = 512) {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
  }

  static isString(value: any) {
    return typeof value === 'string' || value instanceof String;
  }

  static chunkString(str: string, length: string) {
    return str.match(new RegExp('.{1,' + length + '}', 'g'));
  }

  static addMonthsToDate(baseDate: Date, months: number): Date {
    const getDaysInMonth = (year: number, month: number) => new Date(year, month, 0).getDate();

    const date = new Date(baseDate);
    date.setDate(1);
    date.setMonth(date.getMonth() + months);
    date.setDate(Math.min(baseDate.getDate(), getDaysInMonth(date.getFullYear(), date.getMonth() + 1)));
    return date;
  }

  static median(values: number[]) {
    if (values.length === 0) return 0;

    values.sort(function (a, b) {
      return a - b;
    });

    const half = Math.floor(values.length / 2);

    if (values.length % 2) return values[half];

    return (values[half - 1] + values[half]) / 2.0;
  }

  static round(value: number, decimals: number) {
    const d = Math.pow(10, decimals);
    return Math.round(value * d) / d;
  }

  static boundaryToLatLngLiteral(bound: BoundaryDto[]): google.maps.LatLngLiteral[][][] {
    const polygons: google.maps.LatLngLiteral[][][] = [];
    // each array of LatLngLiterals leads to a new outer boundary
    // for holes the order of the LatLngLiterals has to be reversed
    for (const boundary of bound) {
      // add boundary to package list
      const polygon: google.maps.LatLngLiteral[][] = [];
      const boundaryPath = HelperFunctions.geoCoordinatesToLatLngLiteral(boundary.boundary!);
      const boundaryClockwise = HelperFunctions.isClockwisePath(boundary.boundary!);
      polygon.push(boundaryPath);
      if (boundary.holes) {
        for (const hole of boundary.holes) {
          const holeClockwise = HelperFunctions.isClockwisePath(hole);
          // if both are in the same direction -> invert hole
          const inverted = boundaryClockwise == holeClockwise;
          polygon.push(HelperFunctions.geoCoordinatesToLatLngLiteral(hole, inverted));
        }
      }
      polygons.push(polygon);
    }
    return polygons;
  }

  static geoCoordinatesToLatLngLiteral(coordinates: GeoCoordinateDto[], inverted?: boolean): google.maps.LatLngLiteral[] {
    const convertedCoordinates: google.maps.LatLngLiteral[] = [];
    if (inverted) {
      for (let i = coordinates.length - 1; i >= 0; i--) {
        const coo = coordinates[i];
        if (coo) {
          convertedCoordinates.push({ lat: coo.latitude!, lng: coo.longitude! });
        }
      }
    } else {
      for (let i = 0; i < coordinates.length; i++) {
        const coo = coordinates[i];
        if (coo) {
          convertedCoordinates.push({ lat: coo.latitude!, lng: coo.longitude! });
        }
      }
    }
    return convertedCoordinates;
  }

  static isClockwisePath(path: GeoCoordinateDto[]) {
    const pathConv: google.maps.LatLngLiteral[] = [];
    for (const pathPoint of path) {
      pathConv.push({ lat: pathPoint.latitude!, lng: pathPoint.longitude! });
    }
    return HelperFunctions.isClockwisePath_(pathConv);
  }

  static isClockwisePath_(
    path: (google.maps.LatLng | google.maps.LatLngLiteral)[] | google.maps.MVCArray<google.maps.LatLng | google.maps.LatLngLiteral>
  ) {
    // negative polygon areas have counter-clockwise paths
    const isCounterClockwise = google.maps.geometry.spherical.computeSignedArea(path) < 0;
    return !isCounterClockwise;
  }

  static rearrangeLocationsIfNeeded(locationSets?: MachineLocationSetV2Dto[], maxTimeGapInMin = 5): MachineLocationSetV2Dto[] {
    if (!locationSets) {
      return [];
    }
    const timeGap = maxTimeGapInMin * 60 * 1000;
    // combine same machine sets
    const machineLocationSets: Map<number, MachineLocationSetV2Dto[]> = new Map();
    for (const machSet of locationSets) {
      const machineId = machSet.machine!.id!;
      if (!machineLocationSets.has(machineId)) {
        machineLocationSets.set(machineId, []);
      }
      machineLocationSets.get(machineId)?.push(machSet);
    }

    // for each machine fill the gaps (only if less than 5min apart)
    locationSets = [];
    for (const machineId of machineLocationSets.keys()) {
      const machineLocationSet = machineLocationSets.get(machineId);
      let lastLocationOfPreviousSet: LocationDataV2Dto | undefined = undefined;
      for (const machSet of machineLocationSet!) {
        // fill gaps between sections
        for (const locSet of machSet.locations!) {
          // add last location of previous set to fill the gap in the map
          if (
            lastLocationOfPreviousSet &&
            new Date(locSet.locations![locSet.locations!.length - 1].time!).getTime() -
              new Date(lastLocationOfPreviousSet.time!).getTime() <=
              timeGap
          ) {
            const connectorPoint: LocationDataV2Dto = {
              time: lastLocationOfPreviousSet.time,
              altitude: lastLocationOfPreviousSet.altitude,
              latitude: lastLocationOfPreviousSet.latitude,
              longitude: lastLocationOfPreviousSet.longitude,
              gnssSource: lastLocationOfPreviousSet.gnssSource,
            };
            locSet.locations!.unshift(connectorPoint);
          }
          lastLocationOfPreviousSet = locSet.locations![locSet.locations!.length - 1];
        }
      }
      locationSets = locationSets.concat(machineLocationSet!);
    }

    return locationSets;
  }

  static isEqualStringArray(a?: string[], b?: string[]): boolean {
    if (!a && !b) {
      return true;
    }
    if (!a || !b) {
      return false;
    }
    if (a.length != b.length) {
      return false;
    }
    for (let i = 0; i < a.length; i++) {
      if (a[i] != b[i]) {
        return false;
      }
    }
    return true;
  }
}
